import clsx from 'clsx'
import { ArrowRight, ArrowRightLeftIcon } from 'lucide-react'
import React, { useMemo } from 'react'

import { type SmartBudgetPlanData } from 'components/member_onboarding_v2/types'
import { Button, TextButton, useBreakpoint } from 'slab'
import { type GrapeMoney } from 'types'
import { convertGrapeMoneyToNumber, getLocaleCurrencyStringFromNumber } from 'utils'

import { EmptyPlanCard } from './Empty'
import { ErrorPlanCard } from './Error'
import { LoadingPlanCard } from './Loading'

interface BaseSmartBudgetPlanCardProps {
  planData: SmartBudgetPlanData;
  hideSimilarPlansButton?: boolean;
  viewingSimilarPlans?: boolean;
  onSelectPlan?: () => void;
  onViewSimilarPlans?: () => void;
  onViewPlanDetails?: () => void;
}

type SmartBudgetPlanCardProps =
  | (BaseSmartBudgetPlanCardProps & {
    isSelectedPlan: true;
    remainingAllowance?: number;
    selectedPlanPremium?: GrapeMoney;
  })
  | (BaseSmartBudgetPlanCardProps & {
    isSelectedPlan?: false;
    remainingAllowance: number;
    selectedPlanPremium: GrapeMoney;
  })

const PlanFeature: React.FC<{ feature: string }> = ({ feature }) => (
  <div className='body-sm text-gray-500'>{feature}</div>
)

export function SmartBudgetPlanCard ({
  planData,
  hideSimilarPlansButton = false,
  viewingSimilarPlans = false,
  isSelectedPlan = false,
  selectedPlanPremium,
  remainingAllowance,
  onSelectPlan,
  onViewSimilarPlans,
  onViewPlanDetails,
}: SmartBudgetPlanCardProps) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const borderClass = viewingSimilarPlans && isSelectedPlan ? 'border-thatch-600' : 'border-gray-200'
  const bgClass = isSelectedPlan ? 'bg-white' : 'bg-gray-50'

  const handleButtonClick = () => {
    if (isSelectedPlan && onViewSimilarPlans) {
      onViewSimilarPlans()
    } else if (!isSelectedPlan && onSelectPlan) {
      onSelectPlan()
    }
  }

  const thisPlanPremiumAsNumber = convertGrapeMoneyToNumber(planData.premium)

  const remainderOrOverage = useMemo(() => {
    if (!isSelectedPlan && remainingAllowance !== undefined && selectedPlanPremium) {
      const selectedPlanPremiumAsNumber = convertGrapeMoneyToNumber(selectedPlanPremium)
      const newRemainingAllowance = remainingAllowance + selectedPlanPremiumAsNumber - thisPlanPremiumAsNumber
      const currencyString = getLocaleCurrencyStringFromNumber(Math.abs(newRemainingAllowance))

      if (newRemainingAllowance < 0) {
        return <div className='body text-red-500'><span className='body-sm'>You'll owe </span>{currencyString}</div>
      } else {
        return <div className='body text-green-400'>{currencyString}<span className='body-sm'> remaining</span></div>
      }
    }
    return null
  }, [isSelectedPlan, remainingAllowance, selectedPlanPremium, thisPlanPremiumAsNumber])

  const plansDescription = planData.type === 'medical' ? 'similar' : 'other'
  const isDesktop = breakpointIsAtLeast('xs')

  return (
    <div className={clsx(
      'border rounded-2xl p-4 w-full max-w-[450px]',
      borderClass,
      bgClass
    )}
    >
      {/* Header: Logo, plan name, and carrier name */}
      <div className='flex flex-row gap-4 mb-5 items-center'>
        {planData.carrier_icon_url && planData.carrier_icon_url.length > 0 && (
          <img
            aria-hidden
            src={planData.carrier_icon_url}
            className='h-12 w-12 shrink-0 rounded-full object-cover'
            alt='Carrier logo'
          />
        )}
        <div className='flex flex-col gap-[2px]'>
          {isSelectedPlan && <div className='heading-sm text-main'>{planData.type.charAt(0).toUpperCase() + planData.type.slice(1)} plan</div>}
          {!isSelectedPlan && <div className='body-bold text-main'>{planData.name}</div>}
          <div className='flex flex-row gap-1'>
            {isSelectedPlan && <div className='body-sm-bold text-main'>{planData.name}</div>}
            <div className='body-sm text-gray-500'>{planData.carrier}</div>
          </div>
        </div>
      </div>

      {/* Monthly premium and action button */}
      <div className='mb-3 flex flex-row justify-between items-center'>
        <div className='flex flex-row gap-2 items-center'>
          <div className='body text-main'>
            {getLocaleCurrencyStringFromNumber(thisPlanPremiumAsNumber)}<span className='body-sm'> /mo</span>
          </div>
          {remainderOrOverage}
        </div>
        {!hideSimilarPlansButton && (
          <Button
            leftIcon={isSelectedPlan ? ArrowRightLeftIcon : undefined}
            variant='secondary'
            size='small'
            onClick={handleButtonClick}
            disabled={viewingSimilarPlans && isSelectedPlan}
          >
            {isSelectedPlan ? `View ${plansDescription} plans` : 'Select plan'}
          </Button>)}
      </div>

      {/* Divider */}
      <hr className='border-gray-200 -mx-4 mb-4' />

      {/* Plan Details */}
      <div className='flex flex-row justify-between items-center'>
        <div className='flex flex-row gap-[6px] items-center'>
          <PlanFeature feature={planData.features[0]} />
          <PlanFeature feature='•' />
          <PlanFeature feature={planData.features[1]} />
        </div>
        <div className='body-sm'>
          <TextButton rightIcon={isDesktop ? ArrowRight : undefined} onClick={onViewPlanDetails}>
            {isDesktop ? 'Plan details' : 'Details'}
          </TextButton>
        </div>
      </div>
    </div>
  )
}

SmartBudgetPlanCard.Empty = EmptyPlanCard
SmartBudgetPlanCard.Error = ErrorPlanCard
SmartBudgetPlanCard.Loading = LoadingPlanCard
SmartBudgetPlanCard.displayName = 'SmartBudget.PlanCard'

export default SmartBudgetPlanCard
